import { getBulkDiscount, getBulkDiscountTierAmount } from '@repo/lib';
import { getBulkDiscountsForTicketType } from './getBulkDiscountsForTicketType';
import { getCouponTicketTypeDiscount } from './getCouponTicketTypeDiscount';
import getTicketFeeAmount from './getTicketFeeAmount';

interface TicketTypePricingParams {
  ticketType: { _id: string; feeAmount?: number | string | null };
  unitPrice: number;
  quantity: number;
  ticketTypeDiscounts?: any[];
  coupon?: Record<string, any> | null;
  isTicketTypeCoupon: boolean;
  /** A ticketsAndAddOns amount off, shared across the lines by the caller (sharesTicketAmountOff). */
  sharesAmountOff?: boolean;
  /** This line's share of an amount off the coupon shares with add-ons (sharesTicketAmountOff). */
  couponShare?: number;
}

export interface TicketFeePricing {
  amount: number;
  quantity: number;
  subtotal: number;
  couponDiscountAmount: number;
  totalAmount: number;
}

export interface TicketTypePricing {
  subtotal: number;
  bulkDiscount: Record<string, any> | null;
  bulkDiscountAmount: number;
  couponDiscountAmount: number;
  totalAmount: number;
  fee: TicketFeePricing | null;
}

// Priced per ticket in whole minor units the way createChargeTickets charges it,
// so the ticket card and the cart match the charge to the cent.
export default function getTicketTypePricing({
  ticketType,
  unitPrice,
  quantity,
  ticketTypeDiscounts = [],
  coupon,
  isTicketTypeCoupon,
  sharesAmountOff = false,
  couponShare = 0,
}: TicketTypePricingParams): TicketTypePricing {
  const subtotal = unitPrice * quantity;

  const { bulkDiscount, bulkDiscountAmount } = getBulkDiscount(
    getBulkDiscountsForTicketType(ticketTypeDiscounts, ticketType._id),
    quantity,
    subtotal,
    { perUnit: true },
  );

  // Left uncapped for the coupon step, as the API does before its final floor.
  const tierPerTicket = bulkDiscount
    ? getBulkDiscountTierAmount(bulkDiscount, quantity, subtotal, { perUnit: true }) / quantity
    : 0;
  const discountedPerTicket = unitPrice - tierPerTicket;

  const couponDiscountPerTicket = (amount: number) =>
    getCouponTicketTypeDiscount({ amount, coupon, quantity, ticketTypeId: ticketType._id })
      .couponDiscountAmountPerTicket;

  // A shared amount off is priced across the whole line by the caller, not per ticket.
  const isPerTicketCoupon = isTicketTypeCoupon && !sharesAmountOff;
  const priceCouponPerTicket = isPerTicketCoupon ? couponDiscountPerTicket(discountedPerTicket) : 0;
  const netPricePerTicket = Math.max(0, discountedPerTicket - priceCouponPerTicket);

  // The share takes the prices first, as createChargeTickets charges it, and the fees the rest.
  // createChargeTickets prices each ticket of a shared line in whole cents.
  const linePrice = netPricePerTicket * quantity;
  const priceShare = Math.min(couponShare, Math.round(linePrice));
  const totalAmount = couponShare > 0 ? Math.round(linePrice) - priceShare : linePrice;

  const feeAmount = getTicketFeeAmount(ticketType);
  let fee: TicketFeePricing | null = null;
  if (feeAmount) {
    const feeCouponPerTicket =
      isPerTicketCoupon && coupon?.appliesToTicketFees
        ? Math.max(
            0,
            couponDiscountPerTicket(discountedPerTicket + feeAmount) - priceCouponPerTicket,
          )
        : 0;
    const feeSubtotal = feeAmount * quantity;
    const feeTotalAmount = Math.max(
      0,
      Math.max(0, feeAmount - feeCouponPerTicket) * quantity - (couponShare - priceShare),
    );

    fee = {
      amount: feeAmount,
      quantity,
      subtotal: feeSubtotal,
      couponDiscountAmount: feeSubtotal - feeTotalAmount,
      totalAmount: feeTotalAmount,
    };
  }

  return {
    subtotal,
    bulkDiscount,
    bulkDiscountAmount,
    couponDiscountAmount: subtotal - bulkDiscountAmount - totalAmount,
    totalAmount,
    fee,
  };
}
